import { toLoggableEditorError } from '@/components/tiptap/utils/loggable-editor-error';
import { toSafeEditorErrorSummary } from '@/components/tiptap/utils/safe-editor-error';
import { logger } from '@/utils/logger/logger';
import {
  applyConvertSelectionToSection,
  computeSectionSplit,
  findSectionDepth,
  isFullBodyOfUntitledSection,
} from '@heidi/editor/core';
import { NodeSelection, type EditorState } from '@tiptap/pm/state';
import type { Editor } from '@tiptap/react';

// The pure transaction builders live in @heidi/editor/core (task 5.2); this
// app-side shell owns dispatch, focus, and logging. Re-exported for existing
// direct importers (the co-located suite).
export { applyConvertSelectionToSection } from '@heidi/editor/core';

/**
 * Whether the menus should offer convert-to-section for the current selection.
 * False for in-section ranges conversion would refuse (heading-included, or the
 * entire body of an untitled section). Straddling selections stay offered so
 * the click path can warn. Positive polarity so selectors bind it directly.
 */
export function canOfferSectionConversion(state: EditorState): boolean {
  try {
    const { heading, paragraph, sectionNode } = state.schema.nodes;
    if (!heading || !paragraph || !sectionNode) return true;

    const { from, to } = state.selection;
    if (from === to) return true;
    // NodeSelection of a section sits on the node, so the depth walk would miss it.
    if (
      state.selection instanceof NodeSelection &&
      state.selection.node.type === sectionNode
    ) {
      return false;
    }

    const $from = state.doc.resolve(from);
    const $to = state.doc.resolve(to);
    const fromSectionDepth = findSectionDepth($from);
    const toSectionDepth = findSectionDepth($to);
    if (fromSectionDepth === null || toSectionDepth === null) return true;
    if ($from.before(fromSectionDepth) !== $to.before(toSectionDepth)) return true;

    const split = computeSectionSplit(state, fromSectionDepth, {
      heading,
      paragraph,
      sectionNode,
    });
    // Can't convert: heading-included, no heading, or empty selected blocks.
    if (split === null) return false;
    return !isFullBodyOfUntitledSection(split);
  } catch (error) {
    // Runs on every selection change inside render-time menu selectors, and
    // `computeSectionSplit` can hit a content shape `createChecked` rejects.
    // Show the button rather than crash — the click path fails closed with
    // guidance (`convertSelectionToSection`'s own catch). Typed safe envelope
    // only (task 4.10): the rejection can echo document content.
    logger.error('canOfferSectionConversion failed; offering convert button', {
      error: toLoggableEditorError(
        toSafeEditorErrorSummary(error),
        'content_apply_failed'
      ),
    });
    return true;
  }
}

/**
 * Converts the current text selection into a section with an empty heading for
 * the user to name. Returns `false` when the selection can't map cleanly to a
 * section — straddling a section boundary, starting on a section heading, or
 * covering the entire body of an untitled section — so the caller can surface
 * guidance.
 */
export function convertSelectionToSection(editor: Editor): boolean {
  const { heading, paragraph, sectionNode } = editor.state.schema.nodes;
  if (!heading || !paragraph || !sectionNode) {
    // Should be unreachable: the button is gated on sections being enabled.
    logger.error('convertSelectionToSection: section schema nodes are missing');
    return false;
  }
  try {
    const tr = applyConvertSelectionToSection(editor.state);
    if (!tr) return false;

    editor.view.dispatch(tr.scrollIntoView());
    editor.view.focus();
    return true;
  } catch (error) {
    // Rebuilding sections from a slice can hit a content shape `createChecked`
    // rejects; fail closed (the caller surfaces guidance) rather than crash the
    // editor, since no error boundary wraps the note surface. Typed safe
    // envelope only (task 4.10): the rejection can echo document content.
    logger.error('Failed to convert selection to section', {
      error: toLoggableEditorError(
        toSafeEditorErrorSummary(error),
        'content_apply_failed'
      ),
    });
    return false;
  }
}
